import React, { useState } from "react";
import styled from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import { Icon } from "@vds/icons";
import { Tabs, Tab } from "@vds/tabs";
import { ListGroup } from "@vds/lists";
import {
  toggleAssistantPanel,
  setActiveTab,
  toggleFavoritePrompt,
  patchCustomdata,
} from "../../../store/actions/assistantPrompts-actions";
import { getUserCustomdataEndpoint } from "../../../configuration";

const PanelOverlay = styled.div`
  display: block;
  position: fixed;
  top: 60px;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 10;
  background: rgba(0, 0, 0, 0.4);

  @media (min-width: 769px) {
    display: none;
  }
`;

const PanelContainer = styled.div`
  position: fixed;
  right: 0;
  top: 60px;
  bottom: 0;
  width: 100vw;
  height: calc(100vh - 60px);
  z-index: 11;
  background: #ffffff;
  box-shadow: -4px 0 24px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;

  @media (min-width: 769px) {
    position: static;
    top: auto;
    width: 320px;
    min-width: 320px;
    height: 100%;
    overflow: hidden;
    z-index: auto;
    box-shadow: -4px 0 12px rgba(0, 0, 0, 0.15);
  }
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  border-bottom: 1px solid #e0e0e0;
`;

const PanelTitle = styled.span`
  font-size: 16px;
  font-weight: 700;
  color: #1a1a1a;
`;

const TabRow = styled.div`
  display: flex;
  align-items: center;
  padding: 0 16px;
  border-bottom: 1px solid #e0e0e0;
`;

const SearchInput = styled.input`
  width: calc(100% - 32px);
  margin: 12px 16px;
  padding: 8px 12px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  font-size: 14px;
  box-sizing: border-box;
`;

const PromptListScroller = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0 8px;
`;

const PromptItemRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 10px 8px;
  border-radius: 4px;
  cursor: pointer;
  gap: 8px;
  &:hover {
    background: #f5f5f5;
  }
`;

const PromptText = styled.div`
  flex: 1;
  font-size: 14px;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  color: #1a1a1a;
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  min-width: 24px;
  min-height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border-radius: 50%;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: #d1d5db;
  }
`;

const Spinner = styled.span`
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid #d1d5db;
  border-top-color: #1a1a1a;
  border-radius: 50%;
  flex-shrink: 0;
  animation: spin 0.7s linear infinite;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 16px;
  font-size: 14px;
  color: #6f6f6f;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
`;


export default function PromptHistoryPanel({ onSelectPrompt }) {
  const dispatch = useDispatch();
  const [search, setSearch] = useState("");
  const {
    recents,
    favorites,
    activeTab,
    pendingText,
  } = useSelector((s) => s.assistantPrompts);

  const tabIndex = activeTab === "recents" ? 0 : 1;
  const session = useSelector((s) => s.sessionReducer?.activeSession?.activeSession);
  const accountId = session?.accountId;
  const userId = session?.userId || accountId;

  if (window.REACT_APP_FEATURE_PROMPT_FAVORITES !== "enabled") return null;

  const items = (activeTab === "recents" ? recents : favorites).slice().reverse();
  const filtered = search
    ? items.filter((p) => p.text.toLowerCase().includes(search.toLowerCase()))
    : items;
  const favoriteTexts = new Set(favorites.map((f) => f.text));

  const buildNextFavorites = (text, willAdd) => {
    const current = favorites.map((f) => [f.id, f.text]);
    if (willAdd) {
      const id = String(Date.now());
      return current.length < 10 ? [...current, [id, text]] : [...current.slice(1), [id, text]];
    }
    return current.filter(([, t]) => t !== text);
  };

  const handleStarClick = (e, text) => {
    e.stopPropagation();
    const willAdd = !favoriteTexts.has(text);
    dispatch(toggleFavoritePrompt(text));
    const next = buildNextFavorites(text, willAdd);
    dispatch(
      patchCustomdata({
        url: getUserCustomdataEndpoint(),
        method: "patch",
        data: {
          accountname: accountId,
          id: userId,
          customdata: { assistant_prompts: Object.fromEntries(next) },
        },
      })
    );
  };

  const handleRowClick = (text) => {
    onSelectPrompt(text);
  };

  return (
    <>
      <PanelOverlay onClick={() => dispatch(toggleAssistantPanel())} />
      <PanelContainer>
        <PanelHeader>
          <PanelTitle>Recent Activity</PanelTitle>
          <CloseButton onClick={() => dispatch(toggleAssistantPanel())} aria-label="close">
            <Icon name="close" size="small" />
          </CloseButton>
        </PanelHeader>
        <TabRow>
          <Tabs
            orientation="horizontal"
            selectedIndex={tabIndex}
            fillContainer={false}
            id="prompt-history-tabs"
          >
            <Tab
              selectedIndex={tabIndex}
              index={0}
              label="Recent"
              onClick={() => dispatch(setActiveTab("recents"))}
            />
            <Tab
              selectedIndex={tabIndex}
              index={1}
              label="Favorites"
              onClick={() => dispatch(setActiveTab("favorites"))}
            />
          </Tabs>
        </TabRow>
        <SearchInput
          placeholder="Search prompts…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <PromptListScroller>
          {filtered.length === 0 ? (
            <EmptyState>No prompts yet</EmptyState>
          ) : (
            <ListGroup topLine={false} bottomLine={false} surface="light" viewport="desktop">
              {filtered.map((item) => (
                <PromptItemRow key={item.id} onClick={() => handleRowClick(item.text)}>
                  <PromptText>{item.text}</PromptText>
                  {pendingText === item.text ? (
                    <Spinner />
                  ) : (
                    <ActionButton
                      onClick={(e) => handleStarClick(e, item.text)}
                      aria-label={favoriteTexts.has(item.text) ? "unfavorite" : "favorite"}
                    >
                      <Icon
                        name={
                          activeTab === "favorites"
                            ? "trash"
                            : favoriteTexts.has(item.text)
                            ? "added-to-favorite"
                            : "add-to-favorite"
                        }
                        size="small"
                      />
                    </ActionButton>
                  )}
                </PromptItemRow>
              ))}
            </ListGroup>
          )}
        </PromptListScroller>
      </PanelContainer>
    </>
  );
}
